import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Award, Truck, ShieldCheck, Mail, Sparkles } from "lucide-react";
import { PRODUCTS } from "../mock";
import { useToast } from "../hooks/use-toast";
import { submitForm } from "../lib/submit";

export default function ShopPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", interest: "3m", honey: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || busy) return;
    setBusy(true);

    await submitForm(
      {
        name: form.name,
        email: form.email,
        interest: form.interest,
      },
      {
        subject: `FOYEVOO Reserve Signup \u2014 ${form.email}`,
        source: "Shop Page (Reserve)",
        honeypot: form.honey,
      }
    );

    setSubmitted(true);
    setBusy(false);
    toast({ title: "You're on the list", description: "We'll be in touch before launch." });
  };

  return (
    <div className="bg-[#f4ede0] text-[#1a2a38]">
      {/* Top notification strip */}
      <div className="bg-[#1e3a52] text-[#f4ede0] text-center px-4 py-2 text-sm">
        <Sparkles size={14} className="inline mr-2 text-[#d4a857]" />
        Launching soon &mdash; join the waitlist for early access &amp; pre-launch pricing.
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-16">
        {/* Left: product gallery */}
        <div>
          <div className="sticky top-32">
            <div className="relative bg-[#ebe2cd] rounded-md p-8 aspect-square overflow-hidden">
              <img
                src="https://images.pexels.com/photos/25745503/pexels-photo-25745503.jpeg"
                alt="Fountain of Youth Extra Virgin Olive Oil"
                className="w-full h-full object-cover rounded-md"
              />
              <div className="absolute top-4 left-4 bg-[#d4a857] text-[#0f2c44] text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm font-medium">
                2025 Harvest &middot; Coming Soon
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4">
              {[
                "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5",
                "https://images.unsplash.com/photo-1618100790047-3da47dcd5727",
                "https://images.pexels.com/photos/34785886/pexels-photo-34785886.jpeg",
                "https://images.pexels.com/photos/11086294/pexels-photo-11086294.jpeg",
              ].map((src, i) => (
                <div key={i} className="aspect-square bg-[#ebe2cd] rounded-sm overflow-hidden border border-[#4a7ba1]/20">
                  <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: product info + waitlist */}
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Reserve your spot</p>
          <h1 className="text-4xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            High Potency Phenolic Olive Oil
          </h1>

          <p className="mt-6 text-[#3a4a58] leading-relaxed text-lg">
            2,236 mg/kg verified polyphenols. 11,636 mg/kg total bioactive compounds. Each nitrogen-sealed monodose preserves full potency from grove to glass.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {["30 mono-doses", "Oxidation-proof", "Cold-stored", "Lab-verified"].map((t, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full border border-[#4a7ba1]/40 text-[#1e3a52]">{t}</span>
            ))}
          </div>

          <Link to="/analysis" className="inline-block mt-4 text-sm text-[#4a7ba1] underline hover:text-[#d4a857]">
            View 2025 lab analysis &rarr;
          </Link>

          {/* Supply tiers (display only, no pricing) */}
          <div className="mt-8">
            <h3 className="text-sm tracking-widest uppercase text-[#d4a857] mb-4">Planned supply options</h3>
            <div className="grid grid-cols-2 gap-3">
              {PRODUCTS.map((p) => (
                <div
                  key={p.id}
                  className="p-4 rounded-sm border border-[#4a7ba1]/30 bg-[#f4ede0]"
                >
                  <div className="text-lg text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</div>
                  <div className="text-xs mt-1 text-[#3a4a58]">{p.pods} pods &middot; one per day</div>
                  {p.badge && (
                    <div className="text-[10px] uppercase tracking-widest text-[#d4a857] mt-2 font-medium">{p.badge}</div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#3a4a58]/70 mt-3 italic">
              Pricing will be confirmed at launch. Waitlist members receive locked-in pre-launch pricing.
            </p>
          </div>

          {/* Waitlist form */}
          <div className="mt-10 p-6 bg-[#1e3a52] text-[#f4ede0] rounded-sm">
            <div className="flex items-center gap-3 mb-3">
              <Mail size={20} className="text-[#d4a857]" strokeWidth={1.5} />
              <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Join the waitlist</h3>
            </div>
            <p className="text-sm text-[#f4ede0]/80 mb-5 leading-relaxed">
              Be among the first to receive the 2025 harvest. Early access, pre-launch pricing, and harvest updates &mdash; nothing else, no spam.
            </p>

            {submitted ? (
              <div className="p-5 bg-[#0f2c44] border border-[#d4a857]/30 rounded-sm text-center">
                <Check size={24} className="mx-auto text-[#d4a857]" />
                <h4 className="mt-2 text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>You're on the list.</h4>
                <p className="mt-1 text-sm text-[#f4ede0]/75">We'll email {form.email} when allocations open.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid gap-3">
                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.honey}
                  onChange={(e) => setForm({ ...form, honey: e.target.value })}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                />
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="px-4 py-3 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857]"
                />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address *"
                  className="px-4 py-3 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] placeholder-[#f4ede0]/40 focus:outline-none focus:border-[#d4a857]"
                />
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  className="px-4 py-3 bg-[#0f2c44]/60 border border-[#f4ede0]/20 rounded-sm text-[#f4ede0] focus:outline-none focus:border-[#d4a857]"
                >
                  <option value="1m">Most interested in: 1 Month Supply</option>
                  <option value="3m">Most interested in: 3 Months Supply</option>
                  <option value="6m">Most interested in: 6 Months Supply</option>
                  <option value="12m">Most interested in: 12 Months Supply</option>
                  <option value="undecided">Undecided &mdash; tell me more</option>
                </select>
                <button
                  type="submit"
                  disabled={busy}
                  className="bg-[#d4a857] hover:bg-[#b8893d] disabled:opacity-60 text-[#0f2c44] py-3 rounded-sm font-medium transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px" }}
                >
                  {busy ? "Submitting\u2026" : "Reserve your spot \u2192"}
                </button>
                <p className="text-[11px] text-[#f4ede0]/60 mt-1">We never share your information with anyone.</p>
              </form>
            )}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="py-3 border border-[#4a7ba1]/20 rounded-sm">
              <Truck size={20} className="mx-auto text-[#4a7ba1]" strokeWidth={1.4} />
              <div className="text-xs mt-1.5 text-[#3a4a58]">Carbon-negative shipping</div>
            </div>
            <div className="py-3 border border-[#4a7ba1]/20 rounded-sm">
              <ShieldCheck size={20} className="mx-auto text-[#4a7ba1]" strokeWidth={1.4} />
              <div className="text-xs mt-1.5 text-[#3a4a58]">30-day guarantee at launch</div>
            </div>
            <div className="py-3 border border-[#4a7ba1]/20 rounded-sm">
              <Award size={20} className="mx-auto text-[#4a7ba1]" strokeWidth={1.4} />
              <div className="text-xs mt-1.5 text-[#3a4a58]">Lab-verified batch</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why different */}
      <section className="bg-[#ebe2cd]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <h2 className="text-3xl md:text-4xl font-light text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Why Fountain of Youth is different
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {[
              { t: "Polyphenol content", b: "2,236 mg/kg verified using LC-MS/MS at IOC-accredited labs. Most 'high-polyphenol' oils carry 300-500 mg/kg." },
              { t: "Pharmaceutical-grade testing", b: "Independent verification using liquid chromatography mass spectrometry, the analytical gold standard. Every batch." },
              { t: "Nitrogen-sealed freshness", b: "Bottled oils oxidise within weeks once opened. Our nitrogen-flushed pods preserve potency for 18+ months." },
              { t: "Regenerative agriculture", b: "16 kg of CO\u2082 sequestered per litre produced. Hand-picked, cold-extracted within 4 hours." },
            ].map((x, i) => (
              <div key={i} className="bg-[#f4ede0] border border-[#4a7ba1]/15 rounded-sm p-6">
                <h3 className="text-xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{x.t}</h3>
                <p className="mt-3 text-[#3a4a58] leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/the-edge" className="inline-flex items-center gap-2 text-[#1e3a52] hover:text-[#d4a857] border-b border-[#d4a857] pb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}>
              See why athletes are taking it <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
